import React, { useState, useEffect } from 'react';
import {
  MainContainer,
  ContentWrapper,
  HeaderSection,
  TitleContainer,
  Title,
  Subtitle,
  ButtonsContainer,
  SmallActionButton,
  ButtonText,
  ProjectsList,
} from './styles';
import Accounts from './Accounts/Accounts';
import CustomModal, { useModal } from '../../CustomModal';
import { useSelector } from 'react-redux';
import { createRoot } from "react-dom/client";
import ShowRetirements from "./Accounts/AccountCard/ShowRetirements/ShowRetirements";
import LoginFormWilder from "./LoginFormWilder/LoginFormWilder";
import { socialCarbonService } from '../../../services/socialCarbonService';

const ContasWilder = () => {
  const { modalState, showError, showSuccess, showInfo, hideModal } = useModal();
  const usuarioId = useSelector((state) => state.user?.usuarioId);
  const token = sessionStorage.getItem('Authorization');
  const [accountsData, setAccountsData] = useState(null);
  const [userId, setUserId] = useState(null);
  const [selectedProjects, setSelectedProjects] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (!usuarioId) return;
    setUserId(usuarioId);
  }, [usuarioId]);

  // Carregar contas inicialmente apenas com account_list_view
  useEffect(() => {
    const loadInitialAccounts = async () => {
      if (!userId) return;

      try {
        setIsLoading(true);

        // 🔹 Chama o service em vez do axios diretamente
        const data = await socialCarbonService.getAccountListView(token);

        setAccountsData(data.accounts);
      } catch (error) {
        console.error('❌ Erro ao carregar contas:', error.response?.data || error.message);
        showError('Erro ao carregar contas', 'Não foi possível carregar as contas existentes.');
      } finally {
        setIsLoading(false);
      }
    };

    loadInitialAccounts();
  }, [userId, token]);

  // Função para autenticar e obter sessão (usada apenas no botão atualizar)
  const fetchSessionAndAuthenticate = async (credentials) => {
    try {
      const token = sessionStorage.getItem('Authorization');

      // 1️⃣ Autentica o usuário
      await socialCarbonService.authenticateUser(credentials, token);

      // 2️⃣ Obtém a sessão do usuário autenticado
      const sessionData = await socialCarbonService.getUserSession(credentials, token);

      return sessionData;
    } catch (error) {
      console.error('❌ Erro na requisição:', error.response?.data || error.message);
      throw error;
    }
  };

  // Função para sincronizar contas com a Wilder (usada apenas no botão atualizar)
  const syncAccountsWithWilder = async (sessionData) => {
    if (!userId || !sessionData) return;

    const sessao = sessionData?.success?.response?.session;
    if (!sessao) {
      throw new Error("Sessão não encontrada");
    }

    try {
      const token = sessionStorage.getItem("Authorization");

      // 🔄 Chama o serviço centralizado
      await socialCarbonService.syncAccountsWithWilder(userId, sessao, token);

    } catch (error) {
      console.error("Erro ao sincronizar contas:", error.response?.data || error.message);
      throw error;
    }
  };

  // Função para buscar contas atualizadas (após sincronização)
  const fetchUpdatedAccounts = async () => {
    try {
      const token = sessionStorage.getItem('Authorization');

      // ✅ Agora chama o método centralizado
      const accounts = await socialCarbonService.fetchUpdatedAccounts(token);

      return accounts;
    } catch (error) {
      console.error("Erro ao buscar contas atualizadas:", error.response?.data || error.message);
      throw error;
    }
  };

  const handleUpdateRetirements = async () => {
    console.log('Botão clicado - método handleUpdateRetirements chamado!');
    setIsLoading(true);

    try {
      const token = sessionStorage.getItem('Authorization');

      // ✅ Agora utiliza o service
      const response = await socialCarbonService.updateRetirementsFromWilder(token);

      console.log('aposentadorias:', response);
      // Aqui você pode atualizar o estado com os dados recebidos
      // setRetirements(response);

    } catch (error) {
      console.error('erro ao atualizar aposentadorias:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleUpdateAccounts = async () => {
    const container = document.createElement("div");
    document.body.appendChild(container); // Adicione imediatamente ao DOM

    const root = createRoot(container);

    const safeClose = () => {
      try {
        root.unmount();
        if (container.parentNode === document.body) {
          document.body.removeChild(container);
        }
      } catch (error) {
        console.warn('Erro ao fechar modal:', error);
      }
    };

    const handleSubmit = async (credentials) => {
      // Fechar o componente imediatamente
      safeClose();

      // Mostrar mensagem de aguarde via modal personalizado
      showInfo('Processando...', 'Autenticando e sincronizando contas');

      try {
        // 1. Autenticar e obter sessão
        const session = await fetchSessionAndAuthenticate(credentials);

        // 2. Sincronizar contas com a Wilder
        await syncAccountsWithWilder(session);

        // 3. Buscar contas atualizadas
        const novasContas = await fetchUpdatedAccounts();

        // 4. Atualizar o estado com as novas contas
        setAccountsData(novasContas);

        showSuccess('Sucesso!', 'Contas atualizadas com sucesso', () => hideModal());

      } catch (error) {
        console.error('Erro ao atualizar contas:', error);
        showError(
          'Erro ao atualizar contas',
          'Usuário, senha ou PIN incorretos, ou problema na sincronização.',
          () => {
            hideModal();
            handleUpdateAccounts();
          }
        );
      } finally {
        hideModal();
      }
    };

    root.render(
      <LoginFormWilder
        onSubmit={handleSubmit}
        onClose={safeClose}
      />
    );

  };

  return (
    <>
      <MainContainer>
        <ContentWrapper>
          <HeaderSection>
            <TitleContainer>
              <Title>Meus ativos</Title>
              <Subtitle>Atualize os ativos com a API da Social Carbon</Subtitle>
            </TitleContainer>

            <ButtonsContainer>
              <SmallActionButton
                onClick={handleUpdateAccounts}
                disabled={isLoading}
              >
                <ButtonText>
                  {isLoading ? 'Carregando...' : 'Atualizar \n Ativos'}
                </ButtonText>
              </SmallActionButton>

              <SmallActionButton
                onClick={handleUpdateRetirements}
                disabled={isLoading}
              >
                <ButtonText>
                  {isLoading ? 'Carregando...' : 'Atualizar \n Aposentadorias'}
                </ButtonText>
              </SmallActionButton>
            </ButtonsContainer>

          </HeaderSection>

          {isLoading ? (
            <div style={{ textAlign: 'center', padding: '20px' }}>
              Atualizando dados...
            </div>
          ) : (
            <>
              <Accounts accounts={accountsData} />

              {/* Removido bloco antigo com estados não definidos (isLoginOpen/handleLoginSubmit/handleLoginCancel) */}

              {selectedProjects && (
                <ProjectsList>
                  <div style={{ background: "var(--surface-1)", borderRadius: "10px" }}>
                    <ShowRetirements
                      projects={selectedProjects}
                      onClose={() => setSelectedProjects(null)}
                    />
                  </div>
                </ProjectsList>
              )}
            </>
          )}

        </ContentWrapper>
      </MainContainer>
      <CustomModal
        isOpen={modalState.isOpen}
        onClose={hideModal}
        type={modalState.type}
        title={modalState.title}
        message={modalState.message}
        confirmText={modalState.confirmText}
        onConfirm={modalState.onConfirm}
        showCancel={modalState.showCancel}
        cancelText={modalState.cancelText}
        onCancel={modalState.onCancel}
      />
    </>
  );
};

export default ContasWilder;